import { CUSTOMER_LEVEL } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormGroup,
  Icon,
  Label,
  Modal,
  Row,
  SoftBadge,
  Table,
  Text,
  Title,
  useConfiguredForm,
} from '@wyz/ui';
import { SUPPLIERS_TYPES_IDS, useModalState } from '@wyz/utils';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { TextField } from '~/common/components';
import { Trans } from '~/common/context';
import { useManageSupplierTemplates } from '~/connect/api';
import { useCustomerFormLevel } from '~/connect/hooks';

import { SUPPLIER_TYPE_ID_TRANSLATIONS } from '~/common/translators';
import type {
  CustomerFormProps,
  ICustomerSuppliersForm,
  IEmbeddedSupplier,
} from '~/connect/types';

const SuppliersForm = ({ forms, internalCriteria }: CustomerFormProps) => {
  const configurationContext = useConfiguredForm();

  const watchedCriteria = forms.CRITERIA_FORM.methods.watch('criteria');
  const watchedLevel = useCustomerFormLevel({
    parent: forms.GENERAL_FORM.methods.getValues('parent'),
  });

  const [modal, handlers, selectedSupplier] = useModalState<
    'CONFIGURE_SUPPLIER',
    {
      supplier: IEmbeddedSupplier;
      fieldIndex: number;
      isNew: boolean;
    }
  >({
    defaultValue: undefined,
  });
  const methods = useFormContext<ICustomerSuppliersForm>();
  const hasSelectedCriteria =
    watchedCriteria.length > 0 &&
    watchedCriteria.every(
      (criterion) => criterion.criterion.id && criterion.value,
    );

  const manageSupplierTemplates = useManageSupplierTemplates({
    getMatched: {
      enabled: hasSelectedCriteria,
      args: {
        customerCriteria: watchedCriteria
          ? watchedCriteria.map((item) => ({
              id: item.criterion.id,
              valueId: item.value,
            }))
          : [],
        customerInternalCriteria: internalCriteria,
      },
    },
  });

  const { fields: supplierConfigurations, update } = useFieldArray<
    ICustomerSuppliersForm,
    'supplierConfigurations',
    'key'
  >({
    control: methods.control,
    name: 'supplierConfigurations',
    keyName: 'key',
  });

  return (
    <>
      <Card>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Trans i18nKey='app.common.suppliers' />
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <FormGroup>
              <Label>
                <Trans i18nKey='app.entity.form.supplier_template.label' />
              </Label>
              <Text heading='normal' weight='semi-bold'>
                {manageSupplierTemplates.getMatched.isFetching ? (
                  <Icon icon='spinner' size='xs' />
                ) : (
                  (manageSupplierTemplates.getMatched.data?.data?.name ?? '-')
                )}
              </Text>
            </FormGroup>
          </Row>
          <hr />

          <div className='bg-neutral-grey-1 border rounded-3 border-neutral-grey-4 p-3'>
            <Table>
              <thead>
                <tr className='fs-xs'>
                  <th>
                    <Trans i18nKey='app.common.rank' />
                  </th>
                  <th>
                    <Trans i18nKey='app.entity.form.suppliers_table.name_header' />
                  </th>
                  <th>
                    <Trans i18nKey='app.entity.form.suppliers_table.type_header' />
                  </th>
                  <th>
                    <Trans i18nKey='app.common.status' />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody className='text-title-color fw-semi-bold'>
                {manageSupplierTemplates.getMatched.data?.data?.suppliers.map(
                  (supplier, index) => {
                    const configFoundIndex = supplierConfigurations.findIndex(
                      (config) => config.wyzApiId === supplier.wyzApiId,
                    );
                    const configFieldIndex =
                      configFoundIndex !== -1
                        ? configFoundIndex
                        : supplierConfigurations.length;
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{supplier.name}</td>
                        <td>
                          <Trans
                            i18nKey={
                              SUPPLIER_TYPE_ID_TRANSLATIONS[supplier.type]
                            }
                          />
                        </td>
                        <td>
                          {supplier.orderable ? (
                            <SoftBadge bg='success' pill>
                              <Trans i18nKey='app.common.status.orderable' />
                            </SoftBadge>
                          ) : (
                            <SoftBadge bg='warning' pill>
                              <Trans i18nKey='app.common.status.not_orderable' />
                            </SoftBadge>
                          )}
                        </td>
                        <td>
                          {configurationContext.mode === 'WRITE' &&
                            supplier.type !==
                              SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR && (
                              <>
                                {watchedLevel <= CUSTOMER_LEVEL.N2 && (
                                  <>
                                    {configFoundIndex !== -1 ? (
                                      <Icon
                                        className='text-neutral-grey-6'
                                        icon='pencil-line'
                                        width='1.5rem'
                                        height='1.5rem'
                                        onClick={() => {
                                          handlers.open('CONFIGURE_SUPPLIER', {
                                            supplier,
                                            fieldIndex: configFieldIndex,
                                            isNew: false,
                                          });
                                        }}
                                      />
                                    ) : (
                                      <Icon
                                        className='text-neutral-grey-6'
                                        icon='plus'
                                        width='1.5rem'
                                        height='1.5rem'
                                        onClick={() => {
                                          update(configFieldIndex, {
                                            wyzApiId: supplier.wyzApiId,
                                          });
                                          handlers.open('CONFIGURE_SUPPLIER', {
                                            supplier,
                                            fieldIndex: configFieldIndex,
                                            isNew: true,
                                          });
                                        }}
                                      />
                                    )}
                                  </>
                                )}
                              </>
                            )}
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
      {modal === 'CONFIGURE_SUPPLIER' && selectedSupplier && (
        <SupplierConfigurationModal
          onClose={handlers.close}
          supplier={selectedSupplier.supplier}
          configSupplierIndex={selectedSupplier.fieldIndex}
          isAdding={selectedSupplier.isNew}
        />
      )}
    </>
  );
};

type SupplierConfigurationModalProps = {
  onClose: () => void;
  supplier: IEmbeddedSupplier;
  configSupplierIndex: number;
  isAdding: boolean;
};
const SupplierConfigurationModal = ({
  onClose,
  supplier,
  configSupplierIndex,
  isAdding,
}: SupplierConfigurationModalProps) => {
  return (
    <Modal.Container open={true} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>
          <Trans
            i18nKey={
              isAdding
                ? 'app.entity.form.suppliers_config.add_title'
                : 'app.entity.form.suppliers_config.edit_title'
            }
            values={{
              supplierName: supplier.name,
            }}
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col sm={6}>
            <TextField
              name={`supplierConfigurations.${configSupplierIndex}.buyerId`}
              label={
                <Trans i18nKey='app.entity.form.billing_information.buyer_id' />
              }
            />
          </Col>
          {(
            [
              SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
              SUPPLIERS_TYPES_IDS.CENTRAL_STOCK,
            ] as Array<number>
          ).includes(supplier.type) && (
            <Col sm={6}>
              <TextField
                name={`supplierConfigurations.${configSupplierIndex}.dmsRef`}
                label={
                  <Trans i18nKey='app.entity.form.configuration.dms_ref' />
                }
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={6}>
            <TextField
              name={`supplierConfigurations.${configSupplierIndex}.orderingId`}
              label={
                <Trans i18nKey='app.entity.form.configuration.ordering_id' />
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <TextField
              name={`supplierConfigurations.${configSupplierIndex}.consigneeId`}
              label={
                <Trans i18nKey='app.entity.form.configuration.consignee_id' />
              }
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Flex className='center space-x-2'>
          <Button
            id='close-modal'
            variant='danger'
            className='mx-2'
            onClick={onClose}
          >
            <Trans i18nKey='app.common.button.close' />
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal.Container>
  );
};

export default SuppliersForm;
